// ============================================================
// خواندن یک نتیجه با صدا
//
// جدول برای چشم ساخته شده: سرستون بالا، مقدارها پایین، و چشم بین
// آن‌ها بالا و پایین می‌رود. گوش این کار را نمی‌تواند بکند. پس برای
// خواندن، هر ردیف باید به یک جمله‌ی کامل تبدیل شود که برچسب و مقدار
// کنار هم باشند:
//
//   «رکورد اول: وضعیت کم‌فروشی، شهرستان دلفان، نام نانوا فرشته
//    رضای درده، کم‌فروشی ۸.۲، مرحله توزیع مرحله دوم شهریور ۱۴۰۵…»
//
// این کار برای *هر* صفحه‌ای کار می‌کند، چون از همان ستون‌هایی
// می‌خواند که در جدول نشان داده شده‌اند.
// ============================================================

import { Column, QueryResult } from "./datasets";
import { toPersianDigits } from "./text";

/** بیشتر از این تعداد ردیف را کسی با گوش دنبال نمی‌کند. */
const MAX_SPOKEN_ROWS = 6;

const ORDINALS = [
    "اول", "دوم", "سوم", "چهارم", "پنجم", "ششم",
    "هفتم", "هشتم", "نهم", "دهم",
];

const ordinal = (index: number): string =>
    ORDINALS[index] || toPersianDigits(index + 1) + "اُم";

/** مقدارهای تهی که خواندنشان فقط وقت می‌گیرد. */
const isEmpty = (value: string): boolean =>
    !value || value === "—" || value === "-" || value === "ثبت نشده";

const cellText = (column: Column, row: any): string => {
    const raw = row[column.key];
    // format از JSON رد نمی‌شود؛ بعد از یک ناوبری ممکن است نباشد و
    // همان مقدار خام خوانده می‌شود — بهتر از نخواندن.
    const value = column.format ? column.format(raw, row) : raw;
    return String(value === null || value === undefined ? "" : value).trim();
};

/** یک ردیف → یک جمله. */
export const narrateRow = (columns: Column[], row: any): string => {
    const parts: string[] = [];

    columns.forEach((column) => {
        const value = cellText(column, row);
        if (isEmpty(value)) return;
        parts.push(column.label + " " + toPersianDigits(value));
    });

    return parts.join("، ");
};

/**
 * کل نتیجه → متنی که خوانده می‌شود.
 *
 * اگر ردیف‌ها بیشتر از سقف باشند، تعدادشان گفته می‌شود و بقیه
 * خوانده نمی‌شوند — وگرنه کاربر باید دو دقیقه به فهرست گوش بدهد.
 */
export const narrateResult = (result: QueryResult): string => {
    if (!result) return "نتیجه‌ای برای خواندن ندارم.";

    // کارت جزئیات (یک رکورد دقیق) از خود جدول خواناتر است
    if (result.detail && result.detail.length) {
        const lines = result.detail
            .filter((item) => !isEmpty(String(item.value || "")))
            .map((item) => item.label + ": " + toPersianDigits(String(item.value)));
        return result.title + ". " + lines.join("، ") + ".";
    }

    const rows = result.rows || [];
    if (!rows.length) return "در «" + result.title + "» رکوردی پیدا نشد.";

    const shown = rows.slice(0, MAX_SPOKEN_ROWS);
    const head =
        toPersianDigits(result.total || rows.length) + " رکورد در «" + result.title + "»" +
        (rows.length > shown.length || (result.total || 0) > shown.length
            ? "؛ " + toPersianDigits(shown.length) + " مورد اول را می‌خوانم."
            : ".");

    const body = shown.map(
        (row, index) => "رکورد " + ordinal(index) + ": " + narrateRow(result.columns, row) + "."
    );

    return [head].concat(body).join("\n");
};
